import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import {
  CalendarDays, Clock, MapPin, Phone, Navigation, Check, X, AlertTriangle,
  ChevronRight, ChevronLeft, CalendarPlus, Edit3, CheckCircle2,
} from "lucide-react";
import { getMedicationColor } from "@/lib/medicationColors";

/**
 * MobileAppointments — single chronological timeline anchored at "Today".
 *   • Past appointments scroll UP, future scrolls DOWN.
 *   • A clearly-labelled "TODAY" marker sits at the boundary; the list
 *     auto-scrolls to it on first render.
 *   • The Drug Administration detail opens as a side panel on screens ≥ md
 *     (left = list, right = detail) and as a bottom sheet on mobile.
 *   • Each card mirrors the desktop Medication Date Card pattern (date +
 *     all medications for that date as coloured chips + completion switch).
 */

function shortDate(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function buildIcsBlob(appt) {
  const dt = (appt.date || "").replace(/-/g, "");
  const tm = (appt.time || "10:00").replace(":", "");
  const dtStart = `${dt}T${tm}00`;
  const dtEnd   = `${dt}T${String((parseInt(tm.slice(0, 2)) + 1) % 24).padStart(2, "0")}${tm.slice(2)}00`;
  const loc = appt.provider?.office_address
    ? `${appt.provider.office_address}, ${appt.provider.city || ""} ${appt.provider.state || ""} ${appt.provider.zip || ""}`.trim()
    : (appt.location || "");
  const summary = `${appt.event_type}: ${appt.print_name || appt.name || ""}`;
  const ics = [
    "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//OncoCadence//EN",
    "BEGIN:VEVENT", `UID:${appt.id}@oncocadence`,
    `DTSTAMP:${new Date().toISOString().replace(/[-:.]/g, "").slice(0, 15)}Z`,
    `DTSTART:${dtStart}`, `DTEND:${dtEnd}`,
    `SUMMARY:${summary}`, `LOCATION:${loc}`,
    `DESCRIPTION:${(appt.description || "").replace(/\n/g, "\\n")}`,
    "END:VEVENT", "END:VCALENDAR",
  ].join("\n");
  return new Blob([ics], { type: "text/calendar" });
}

function ConfirmationBadge({ value }) {
  if (!value) return null;
  const map = {
    confirmed: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Confirmed" },
    tentative: { cls: "bg-amber-50 text-amber-700 border-amber-200",       label: "Tentative" },
    declined:  { cls: "bg-rose-50 text-rose-700 border-rose-200",          label: "Declined" },
  }[value] || { cls: "bg-stone-50 text-stone-700 border-stone-200", label: value };
  return <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${map.cls}`}>{map.label}</span>;
}

function SwitchToggle({ checked, busy, onChange, testid }) {
  return (
    <button
      type="button" role="switch" aria-checked={checked}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onChange(); }}
      disabled={busy} data-testid={testid}
      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none disabled:opacity-60 ${checked ? "bg-emerald-500" : "bg-stone-300"}`}
    >
      <span aria-hidden="true" className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${checked ? "translate-x-5" : "translate-x-0"}`} />
    </button>
  );
}

function DayCard({ group, isFuture, isSelected, onChipClick }) {
  const d = new Date(group.date + "T00:00:00");
  const fullDate = d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  const drugEvents = group.events.filter((e) => e.event_type === "Drug Administration" && e.status !== "skipped");
  const otherEvents = group.events.filter((e) => e.event_type !== "Drug Administration" && e.status !== "skipped");
  const completed = drugEvents.length > 0 && drugEvents.every(
    (e) => e.status === "completed" || e.med_status === "administered",
  );
  const seen = new Set();
  const chips = drugEvents
    .filter((e) => {
      const k = e.print_name || e.drug_name || e.name;
      if (seen.has(k)) return false; seen.add(k); return true;
    })
    .map((e) => ({ event: e, name: e.print_name || e.drug_name || e.name, color: getMedicationColor(e.print_name || e.drug_name || e.name) }));
  const primaryEvent = drugEvents[0] || otherEvents[0] || group.events[0];
  const confirmation = group.events.find((e) => e.confirmation)?.confirmation;
  const dim = !isFuture && !completed ? "opacity-80" : "";
  return (
    <div
      className={`medication-date-card rounded-2xl p-4 ${completed ? "is-completed" : ""} ${isSelected ? "ring-2 ring-brand-500" : ""} ${dim}`}
      data-testid={`day-card-${group.date}`}
    >
      <div className="flex items-start justify-between gap-3">
        <button
          onClick={() => onChipClick(primaryEvent)}
          className="flex-1 text-left"
          data-testid={`day-card-tap-${group.date}`}
        >
          <div className="font-display font-bold text-stone-900 text-base leading-tight">{fullDate}</div>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            {isFuture
              ? <span className="text-[10px] uppercase tracking-wider font-bold text-white bg-brand-500 px-2 py-0.5 rounded-full">Upcoming</span>
              : <span className="text-[10px] uppercase tracking-wider font-bold text-stone-500 bg-stone-100 px-2 py-0.5 rounded-full">Past</span>
            }
            {confirmation && <ConfirmationBadge value={confirmation} />}
            {completed && <span className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />Completed by provider</span>}
          </div>
        </button>
      </div>

      {chips.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {chips.map((c) => (
            <button
              key={c.name}
              onClick={() => onChipClick(c.event)}
              className="px-2 py-1 rounded-lg text-[11px] font-bold truncate max-w-[140px] active:scale-95 transition"
              style={{ backgroundColor: c.color.bg, color: c.color.text }}
              data-testid={`day-chip-${group.date}-${c.name.replace(/\W+/g, "-")}`}
            >
              {c.name}
            </button>
          ))}
        </div>
      )}

      {otherEvents.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {otherEvents.map((e) => (
            <button
              key={e.id}
              onClick={() => onChipClick(e)}
              className="px-2 py-1 rounded-lg text-[11px] font-medium bg-stone-100 text-stone-700 border border-stone-200"
              data-testid={`day-chip-other-${e.id}`}
            >
              {e.event_type}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** The Drug Administration detail content. Used both as a side-panel (≥ md)
 *  and as a bottom-sheet modal (< md). `embedded=true` removes the modal
 *  chrome so it can sit inside the side panel cleanly. */
function AppointmentDetail({ appt, onClose, onReload, embedded = false }) {
  const [busy, setBusy] = useState(false);
  const [arriveBy, setArriveBy] = useState(appt.arrive_minutes_early ?? 15);
  const [newDate, setNewDate] = useState("");
  const [reason, setReason] = useState("");
  const [showReschedule, setShowReschedule] = useState(false);
  const [toast, setToast] = useState("");

  // Reset transient state whenever the user selects a different appointment
  useEffect(() => {
    setArriveBy(appt.arrive_minutes_early ?? 15);
    setShowReschedule(false); setNewDate(""); setReason("");
  }, [appt.id]);

  const flash = (m) => { setToast(m); setTimeout(() => setToast(""), 3000); };
  const setConfirmation = async (confirmation) => {
    setBusy(true);
    try {
      await api.post("/patient/appointments/confirm", { event_id: appt.id, confirmation, arrive_minutes_early: arriveBy });
      flash(`Marked ${confirmation}`);
      onReload();
    } finally { setBusy(false); }
  };
  const submitReschedule = async () => {
    if (!newDate) return;
    setBusy(true);
    try {
      await api.post("/patient/requests", {
        kind: "reschedule_appointment", event_id: appt.id,
        new_date: newDate, message: reason || `Please reschedule to ${newDate}`,
      });
      flash("Reschedule request sent");
      setShowReschedule(false); setReason(""); setNewDate("");
    } finally { setBusy(false); }
  };
  const submitCancel = async () => {
    if (!window.confirm("Send a cancellation request to your care team?")) return;
    setBusy(true);
    try {
      await api.post("/patient/requests", { kind: "other", event_id: appt.id, message: `Please cancel this appointment on ${appt.date}.` });
      flash("Cancellation request sent");
    } finally { setBusy(false); }
  };
  const downloadIcs = () => {
    const url = URL.createObjectURL(buildIcsBlob(appt));
    const a = document.createElement("a");
    a.href = url; a.download = `oncocadence-${appt.id}.ics`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const provider = appt.provider || {};
  const fullAddr = [provider.office_address, provider.city, provider.state ? `${provider.state} ${provider.zip || ""}`.trim() : provider.zip].filter(Boolean).join(", ");
  const gpsHref = fullAddr ? `https://maps.google.com/?q=${encodeURIComponent(fullAddr)}` : null;
  const phoneHref = provider.phone ? `tel:${provider.phone.replace(/[^0-9+]/g, "")}` : null;

  const Body = (
    <div className="px-5 py-4 space-y-4">
      <div>
        <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold">{shortDate(appt.date)}</div>
        <div className="font-display text-xl font-bold text-stone-900 mt-1">{appt.print_name || appt.drug_name || appt.name}</div>
        <div className="text-sm text-stone-600 mt-1">
          {appt.time && <span className="inline-flex items-center gap-1 mr-3"><Clock className="w-4 h-4" />{appt.time}</span>}
          {appt.regimen_name && <span className="text-stone-500">{appt.regimen_name}</span>}
        </div>
        {appt.dose && <div className="text-sm text-stone-600 mt-1">Dose: <span className="font-medium">{appt.dose}</span> · {appt.route || "Intravenous"}</div>}
      </div>

      <div className="bg-stone-50 rounded-2xl p-4">
        <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold mb-2">Confirmation Status</div>
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <ConfirmationBadge value={appt.confirmation || "tentative"} />
          {appt.arrive_minutes_early != null && <span className="text-xs text-stone-600">Arriving {appt.arrive_minutes_early} min early</span>}
        </div>
        <div className="grid grid-cols-3 gap-2">
          <button data-testid="confirm-btn"   disabled={busy} onClick={() => setConfirmation("confirmed")} className="px-3 py-2 rounded-xl bg-emerald-500 text-white text-xs font-semibold disabled:opacity-60"><Check className="w-3.5 h-3.5 inline mr-1" />Confirm</button>
          <button data-testid="tentative-btn" disabled={busy} onClick={() => setConfirmation("tentative")} className="px-3 py-2 rounded-xl bg-amber-500 text-white text-xs font-semibold disabled:opacity-60">Tentative</button>
          <button data-testid="decline-btn"   disabled={busy} onClick={() => setConfirmation("declined")} className="px-3 py-2 rounded-xl bg-rose-500 text-white text-xs font-semibold disabled:opacity-60"><X className="w-3.5 h-3.5 inline mr-1" />Decline</button>
        </div>
      </div>

      <div className="bg-stone-50 rounded-2xl p-4">
        <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold mb-2">Arrive By (minutes early)</div>
        <div className="flex items-center gap-2">
          {[10, 15, 30, 45].map((n) => (
            <button key={n} onClick={() => setArriveBy(n)} data-testid={`arrive-${n}`}
              className={`flex-1 px-3 py-2 rounded-xl text-xs font-semibold border ${arriveBy === n ? "bg-brand-500 text-white border-brand-500" : "bg-white text-stone-700 border-stone-300"}`}>{n} min</button>
          ))}
        </div>
        <label className="block mt-3 text-xs text-stone-600">
          <input data-testid="visit-early-checkbox" type="checkbox" className="mr-2 align-middle" defaultChecked={arriveBy >= 30} onChange={(e) => setArriveBy(e.target.checked ? 30 : 10)} />
          Visit Early? (arrive ≥ 30 min ahead for prep)
        </label>
      </div>

      <div className="bg-stone-50 rounded-2xl p-4">
        <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold mb-2">Treatment Location</div>
        {provider.full_name ? (
          <>
            <div className="font-semibold text-stone-900">{provider.full_name}</div>
            {provider.department && <div className="text-sm text-stone-600">{provider.department}{provider.specialty ? ` · ${provider.specialty}` : ""}</div>}
            {fullAddr && <div className="text-sm text-stone-700 mt-1">{fullAddr}</div>}
            <div className="mt-3 grid grid-cols-2 gap-2">
              <a data-testid="gps-link" href={gpsHref || "#"} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-brand-500 text-white text-xs font-semibold ${!gpsHref ? "opacity-40 pointer-events-none" : ""}`}>
                <Navigation className="w-3.5 h-3.5" />Directions
              </a>
              <a data-testid="phone-link" href={phoneHref || "#"} className={`inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-stone-800 text-white text-xs font-semibold ${!phoneHref ? "opacity-40 pointer-events-none" : ""}`}>
                <Phone className="w-3.5 h-3.5" />{provider.phone || "No phone"}
              </a>
            </div>
            {provider.office_directions && <div className="text-xs text-stone-500 mt-3 italic">"{provider.office_directions}"</div>}
          </>
        ) : (
          <div className="text-sm text-stone-500">No provider details available.</div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <button data-testid="add-to-calendar-btn" onClick={downloadIcs} className="px-3 py-3 rounded-xl bg-white border border-stone-300 text-stone-700 font-semibold text-sm inline-flex items-center justify-center gap-1.5">
          <CalendarPlus className="w-4 h-4" />Add to Calendar
        </button>
        <button data-testid="reschedule-btn" onClick={() => setShowReschedule(true)} className="px-3 py-3 rounded-xl bg-white border border-stone-300 text-stone-700 font-semibold text-sm inline-flex items-center justify-center gap-1.5">
          <Edit3 className="w-4 h-4" />Reschedule
        </button>
      </div>
      <button data-testid="cancel-btn" onClick={submitCancel} className="w-full px-3 py-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 font-semibold text-sm inline-flex items-center justify-center gap-1.5">
        <AlertTriangle className="w-4 h-4" />Request to cancel
      </button>

      {showReschedule && (
        <div className="bg-white border border-stone-200 rounded-2xl p-4 space-y-2.5" data-testid="reschedule-form">
          <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Request a new date</div>
          <input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-stone-300" data-testid="reschedule-new-date" />
          <textarea rows={2} placeholder="Optional note for your care team" value={reason} onChange={(e) => setReason(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-stone-300 text-sm" data-testid="reschedule-reason" />
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => setShowReschedule(false)} className="px-3 py-2 rounded-xl border border-stone-300 text-sm">Cancel</button>
            <button onClick={submitReschedule} disabled={!newDate || busy} className="px-3 py-2 rounded-xl bg-brand-500 text-white text-sm font-semibold disabled:opacity-50" data-testid="submit-reschedule">Send request</button>
          </div>
        </div>
      )}

      {toast && <div className="fixed left-1/2 -translate-x-1/2 bottom-24 px-4 py-2 rounded-full bg-stone-900 text-white text-sm shadow-lg z-50" data-testid="appt-toast">{toast}</div>}
    </div>
  );

  if (embedded) {
    return (
      <div className="bg-white border border-stone-200 rounded-2xl shadow-soft sticky top-4" data-testid="appt-detail-panel">
        <div className="border-b border-stone-200 px-5 py-3 flex items-center justify-between">
          <div className="font-display font-semibold">{appt.event_type}</div>
          <button onClick={onClose} className="text-stone-500 inline-flex items-center gap-1 text-sm" data-testid="detail-close">
            <X className="w-4 h-4" />Close
          </button>
        </div>
        {Body}
      </div>
    );
  }
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end md:hidden" data-testid="appt-detail">
      <div className="bg-white w-full max-w-md mx-auto rounded-t-3xl max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white/95 backdrop-blur border-b border-stone-200 px-5 py-3 flex items-center justify-between">
          <button onClick={onClose} className="text-stone-500 inline-flex items-center gap-1 text-sm">
            <ChevronLeft className="w-4 h-4" />Back
          </button>
          <div className="font-display font-semibold">{appt.event_type}</div>
          <div className="w-12" />
        </div>
        {Body}
      </div>
    </div>
  );
}

export default function MobileAppointments() {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState("future"); // 'future' | 'past'

  const load = () => api.get("/patient/appointments").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  // Group events by date and split into past + future buckets.
  const { futureGroups, pastGroups } = useMemo(() => {
    if (!data) return { futureGroups: [], pastGroups: [] };
    const groupBy = (arr) => {
      const map = new Map();
      arr.forEach((e) => {
        if (e.status === "skipped") return;
        if (!map.has(e.date)) map.set(e.date, { date: e.date, events: [] });
        map.get(e.date).events.push(e);
      });
      return [...map.values()];
    };
    // Future: ascending (next-up first at the top)
    const future = groupBy(data.future || []).sort((a, b) => a.date.localeCompare(b.date));
    // Past: descending (most recent first at the top)
    const past = groupBy(data.past || []).sort((a, b) => b.date.localeCompare(a.date));
    return { futureGroups: future, pastGroups: past };
  }, [data]);

  // When the view changes, clear the selected detail so the right pane only
  // appears once the user picks an item from the visible list.
  useEffect(() => { setSelected(null); }, [view]);

  if (!data) return <div className="max-w-md mx-auto p-6 text-stone-500">Loading…</div>;

  const groups = view === "future" ? futureGroups : pastGroups;

  return (
    <div className="mx-auto pb-4 bg-stone-50 min-h-screen max-w-md md:max-w-6xl" data-testid="mobile-appointments">
      <header className="px-5 pt-6 pb-3 md:px-8">
        <h1 className="font-display text-2xl md:text-3xl font-bold text-stone-900">Appointments</h1>
        <p className="text-stone-500 text-sm">{view === "future" ? "Upcoming first." : "Most recent first."}</p>
      </header>

      {/* Past / Future segmented control */}
      <div className="px-5 md:px-8 mb-3" data-testid="appt-view-switch">
        <div className="inline-flex bg-white border border-stone-200 rounded-xl p-1 shadow-soft">
          <button
            onClick={() => setView("future")}
            data-testid="switch-future"
            className={`px-5 py-2 rounded-lg text-sm font-semibold transition ${view === "future" ? "bg-brand-500 text-white" : "text-stone-700 hover:bg-stone-50"}`}
          >
            Future ({futureGroups.length})
          </button>
          <button
            onClick={() => setView("past")}
            data-testid="switch-past"
            className={`px-5 py-2 rounded-lg text-sm font-semibold transition ${view === "past" ? "bg-stone-700 text-white" : "text-stone-700 hover:bg-stone-50"}`}
          >
            Past ({pastGroups.length})
          </button>
        </div>
      </div>

      <div className="md:grid md:grid-cols-12 md:gap-6 md:px-8">
        {/* Timeline list — scrolls in its OWN container with a visible scrollbar */}
        <section
          className="px-5 md:px-0 md:col-span-5 space-y-2.5 max-h-[70vh] overflow-y-auto pr-2 timeline-scroll"
          data-testid="timeline-list"
        >
          {groups.length === 0 ? (
            <div className="bg-white border border-stone-200 rounded-2xl p-6 text-center text-stone-500 text-sm">
              {view === "future" ? "No upcoming appointments." : "No past appointments yet."}
            </div>
          ) : (
            groups.map((g) => (
              <DayCard
                key={g.date}
                group={g}
                isFuture={view === "future"}
                isSelected={selected && g.events.some((e) => e.id === selected.id)}
                onChipClick={(ev) => setSelected(ev)}
              />
            ))
          )}
        </section>

        {/* Right detail pane — only rendered when an appointment is selected so
            the blank placeholder card no longer appears (per user feedback). */}
        {selected && (
          <section className="hidden md:block md:col-span-7" data-testid="detail-pane">
            <AppointmentDetail
              appt={selected}
              embedded
              onClose={() => setSelected(null)}
              onReload={() => load()}
            />
          </section>
        )}
      </div>

      {/* On phones, show the bottom-sheet modal instead */}
      {selected && (
        <div className="md:hidden">
          <AppointmentDetail
            appt={selected}
            onClose={() => setSelected(null)}
            onReload={() => { load(); setSelected(null); }}
          />
        </div>
      )}
    </div>
  );
}
